import type { Container, Sprite } from "pixi.js";
import type { TweenEasing } from "../../runner/Easing";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { SymbolDimExposurePort } from "../win-highlight/SymbolDimSession";

interface Entry {
  node: Container;
  /** Last-applied exposure value, so a re-ramp starts from where the previous left off. */
  current: number;
  /** Bumped per op on this sprite; a stale in-flight ramp stops writing the node. */
  gen: number;
}

/**
 * Alpha-based implementation of the `SymbolDimExposurePort`.
 * Drives a container's alpha from a signed-exposure `rampExposure(target)` call, no shader needed.
 *
 * Maps the signed exposure linearly to alpha: `alpha = clamp(1 + target, 0, 1)`. Positive targets
 * clamp to 1 — this variant doesn't brighten (alpha can't represent overexposure); it is dim-only.
 *
 * Race safety: per-sprite generation tokens. Engine-time aware: lead-in via `sleep`, ramp via
 * `tweenProgress`. `release` snaps the node back to fully opaque.
 */
export class AlphaDimEffect implements SymbolDimExposurePort {
  private readonly taskRunner: PresentationTaskRunner;
  private readonly entries: Map<Sprite, Entry> = new Map();
  private readonly dimNodeOf: (sprite: Sprite) => Container | null;
  private generation = 0;

  public constructor(
    taskRunner: PresentationTaskRunner,
    opts?: {
      /**
       * Which container carries the alpha for a given sprite. Default: the sprite itself. Pass e.g.
       * `(s) => s.parent` (the cell's content node) so the dim CASCADES over everything at the cell —
       * sprite + any overlay children (idle Spine, labels) alike.
       */
      dimNodeResolver?: (sprite: Sprite) => Container | null;
    },
  ) {
    this.taskRunner = taskRunner;
    this.dimNodeOf = opts?.dimNodeResolver ?? ((sprite) => sprite);
  }

  public rampExposure(
    sprite: Sprite | null,
    target: number,
    seconds: number,
    easing: TweenEasing,
    onComplete?: () => void,
    startDelaySeconds: number = 0,
  ): void {
    if (!sprite || sprite.destroyed) return;
    const node = this.dimNodeOf(sprite);
    if (!node || node.destroyed) return;

    let entry = this.entries.get(sprite);
    if (!entry) {
      node.alpha = 1;
      entry = { node, current: 0, gen: 0 };
      this.entries.set(sprite, entry);
    }
    const e = entry;

    const gen = ++this.generation;
    e.gen = gen;
    const isCurrent = (): boolean => !sprite.destroyed && this.entries.get(sprite)?.gen === gen && !e.node.destroyed;
    const from = e.current;

    void (async () => {
      if (startDelaySeconds > 0) {
        await this.taskRunner.sleep(startDelaySeconds);
        if (!isCurrent()) return;
      }
      if (seconds > 0) {
        await this.taskRunner.tweenProgress(
          seconds,
          (t01) => {
            if (!isCurrent()) return;
            const ex = from + (target - from) * t01;
            e.current = ex;
            e.node.alpha = exposureToAlpha(ex);
          },
          undefined,
          { easing },
        );
      } else {
        if (!isCurrent()) return;
        e.current = target;
        e.node.alpha = exposureToAlpha(target);
      }
      if (!isCurrent()) return;
      onComplete?.();
    })();
  }

  public release(sprite: Sprite): void {
    const entry = this.entries.get(sprite);
    if (!entry) return;
    this.entries.delete(sprite);
    // Invalidate any in-flight ramp on this sprite — its next callback bails on the gen check.
    this.generation++;
    if (!entry.node.destroyed) entry.node.alpha = 1;
  }
}

function exposureToAlpha(exposure: number): number {
  // Linear map: exposure -1 → 0, exposure 0 → 1. Positive clamps (alpha can't brighten).
  return Math.max(0, Math.min(1, 1 + exposure));
}
